/**
 * TanStack Query hooks.
 *
 * A real data layer rather than `useEffect` plus fetch: caching, deduplication, and
 * background refetch come free, and a chart that mounts twice does not fetch twice.
 * The predecessor called axios directly from every component, which meant no cache and
 * no way to invalidate.
 *
 * Stale times are chosen per resource rather than globally. Bar data for a closed
 * session never changes, so caching it for minutes is correct; a sync job's status
 * changes every second.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { ApiError, api } from "./client";
import type {
  AIResponse,
  AuthConfig,
  BacktestSummary,
  BarSeries,
  DataStatusResponse,
  FactorReport,
  FormulaEvalResponse,
  FormulaValidateResponse,
  Frequency,
  AIProvider,
  AISettingsStatus,
  FullSyncSchedule,
  FullSyncScheduleConfig,
  FunctionInfo,
  HealthResponse,
  InviteCode,
  IssuedInvite,
  OptimizeJob,
  SavedFormula,
  ScreenResponse,
  SecurityInfo,
  StrategyIR,
  StrategyRecord,
  StrategyViews,
  SyncJob,
  SyncInput,
  TokenResponse,
  TradeRow,
  TushareTokenStatus,
  UniverseFilter,
  UserOut,
} from "./types";
import type { AdjustMode } from "./types";

export const queryKeys = {
  authConfig: ["auth", "config"] as const,
  me: ["auth", "me"] as const,
  securities: (search?: string) => ["securities", search ?? ""] as const,
  bars: (symbol: string, frequency: Frequency, adjust: AdjustMode) =>
    ["bars", symbol, frequency, adjust] as const,
  formulaFunctions: ["formula", "functions"] as const,
  savedFormulas: (kind?: string) => ["formula", "saved", kind ?? "all"] as const,
  strategies: ["strategies"] as const,
  strategy: (id: number) => ["strategies", id] as const,
  runs: ["backtest", "runs"] as const,
  run: (runId: string) => ["backtest", "runs", runId] as const,
  runTrades: (runId: string) => ["backtest", "runs", runId, "trades"] as const,
  health: ["health"] as const,
  invites: ["auth", "invites"] as const,
  tushareToken: ["settings", "tushare-token"] as const,
  aiSettings: ["settings", "ai"] as const,
  dataStatus: ["data", "status"] as const,
  fullSyncSchedule: ["data", "full-sync-schedule"] as const,
  syncJob: (jobId: string) => ["data", "sync", jobId] as const,
  activeSyncJob: ["data", "sync", "active"] as const,
};

// --------------------------------------------------------------------------- auth

export function useAuthConfig() {
  return useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: () => api.get<AuthConfig>("/auth/config"),
    // Server capability flags, fixed for the process lifetime.
    staleTime: Infinity,
    retry: false,
  });
}

export function useMe(enabled = true) {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: () => api.get<UserOut>("/auth/me"),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (payload: { username: string; password: string }) =>
      api.post<TokenResponse>("/auth/login", payload),
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (payload: { username: string; password: string; invite_code: string }) =>
      api.post<TokenResponse>("/auth/register", payload),
  });
}

export function useUpdatePreferences() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (preferences: Record<string, unknown>) =>
      api.patch<UserOut>("/auth/preferences", preferences),
    onSuccess: (user) => client.setQueryData(queryKeys.me, user),
  });
}

/**
 * Invite codes, owner-only. `enabled` is the caller's role check: a member requesting
 * this list gets a 403, and a query that is guaranteed to fail is noise in the log.
 */
export function useInvites(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.invites,
    queryFn: () => api.get<InviteCode[]>("/auth/invites"),
    enabled,
  });
}

/**
 * Whether the caller is the owner. `undefined` while that is still being determined, so
 * a guard can wait instead of redirecting an owner whose profile has not loaded yet.
 *
 * With auth disabled there is no login and every request is served as the local owner
 * (see `_local_owner` on the server), so the answer is true without asking.
 *
 * This is presentation only. The server enforces the same rule on every owner endpoint;
 * hiding a page from a member is about not showing controls that would 403.
 */
export function useIsOwner(): boolean | undefined {
  const { data: config } = useAuthConfig();
  const authRequired = Boolean(config && !config.auth_disabled);
  const { data: me } = useMe(authRequired);
  if (!config) return undefined;
  if (!authRequired) return true;
  if (!me) return undefined;
  return me.role === "owner";
}

export function useTushareToken(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.tushareToken,
    queryFn: () => api.get<TushareTokenStatus>("/settings/tushare-token"),
    enabled,
    retry: false,
  });
}

function useTushareTokenMutation<TArg>(fn: (arg: TArg) => Promise<TushareTokenStatus>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (status) => {
      client.setQueryData(queryKeys.tushareToken, status);
      // The health check reports where the token comes from, so refresh it too.
      void client.invalidateQueries({ queryKey: queryKeys.health });
    },
  });
}

export function useSaveTushareToken() {
  return useTushareTokenMutation((token: string) =>
    api.put<TushareTokenStatus>("/settings/tushare-token", { token }),
  );
}

export function useDeleteTushareToken() {
  return useTushareTokenMutation((_: void) =>
    api.delete<TushareTokenStatus>("/settings/tushare-token"),
  );
}

export function useAISettings(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.aiSettings,
    queryFn: () => api.get<AISettingsStatus>("/settings/ai"),
    enabled,
    retry: false,
  });
}

function useAISettingsMutation<TArg>(fn: (arg: TArg) => Promise<AISettingsStatus>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (status) => {
      client.setQueryData(queryKeys.aiSettings, status);
      // ai_enabled gates the assistant page; health lists the configured providers.
      void client.invalidateQueries({ queryKey: queryKeys.authConfig });
      void client.invalidateQueries({ queryKey: queryKeys.health });
    },
  });
}

export interface AIProviderInput {
  provider: AIProvider;
  /** Omitted keeps the saved key. */
  api_key?: string;
  base_url: string;
  model: string;
}

export function useSaveAIProvider() {
  return useAISettingsMutation(({ provider, ...body }: AIProviderInput) =>
    api.put<AISettingsStatus>(`/settings/ai/${provider}`, body),
  );
}

export function useDeleteAIProvider() {
  return useAISettingsMutation((provider: AIProvider) =>
    api.delete<AISettingsStatus>(`/settings/ai/${provider}`),
  );
}

export function useSaveAIDefaultProvider() {
  return useAISettingsMutation((provider: AIProvider) =>
    api.put<AISettingsStatus>("/settings/ai/default-provider", { default_provider: provider }),
  );
}

export function useIssueInvite() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ttlDays: number) =>
      api.post<IssuedInvite>(`/auth/invites?ttl_days=${ttlDays}`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.invites }),
  });
}

// ------------------------------------------------------------------- market data

export function useSecurities(search?: string) {
  return useQuery({
    queryKey: queryKeys.securities(search),
    queryFn: () =>
      api.get<SecurityInfo[]>(
        `/securities?limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`,
      ),
    staleTime: 60 * 60_000,
  });
}

export function useBars(
  symbol: string | undefined,
  frequency: Frequency = "1d",
  adjust: AdjustMode = "FORWARD",
  options?: Partial<UseQueryOptions<BarSeries>>,
) {
  return useQuery({
    queryKey: queryKeys.bars(symbol ?? "", frequency, adjust),
    queryFn: () =>
      api.get<BarSeries>(
        `/bars/${symbol}?frequency=${frequency}&adjust=${adjust}&limit=1200`,
      ),
    enabled: Boolean(symbol),
    // A closed session's bars never change, so caching for five minutes is free
    // correctness rather than a tradeoff.
    staleTime: 5 * 60_000,
    ...options,
  });
}

// ----------------------------------------------------------------------- formula

export function useFormulaFunctions() {
  return useQuery({
    queryKey: queryKeys.formulaFunctions,
    queryFn: () => api.get<Record<string, FunctionInfo[]>>("/formula/functions"),
    staleTime: Infinity,
  });
}

export function useSavedFormulas(kind?: string) {
  return useQuery({
    queryKey: queryKeys.savedFormulas(kind),
    queryFn: () =>
      api.get<SavedFormula[]>(`/formula/saved${kind ? `?kind=${kind}` : ""}`),
    staleTime: 5 * 60_000,
  });
}

export function useValidateFormula() {
  return useMutation({
    mutationFn: (payload: { source: string; strict?: boolean }) =>
      api.post<FormulaValidateResponse>("/formula/validate", payload),
  });
}

export function useEvaluateFormula() {
  return useMutation({
    mutationFn: (payload: {
      source: string;
      symbol: string;
      frequency?: Frequency;
      adjust?: AdjustMode;
      parameters?: Record<string, number>;
    }) => api.post<FormulaEvalResponse>("/formula/evaluate", payload),
  });
}

// ------------------------------------------------------------------------ screen

export function useScreen() {
  return useMutation({
    mutationFn: (payload: {
      formula: string;
      as_of?: string;
      universe?: Partial<UniverseFilter>;
      parameters?: Record<string, number>;
      top_n?: number;
    }) => api.post<ScreenResponse>("/screen", payload),
  });
}

// ---------------------------------------------------------------------- strategy

export function useStrategyViews() {
  return useMutation({
    mutationFn: (payload: { ir?: StrategyIR; dsl?: string; name?: string }) =>
      api.post<StrategyViews>("/strategy/views", payload),
  });
}

export function useStrategyExample() {
  return useQuery({
    queryKey: ["strategy", "example"],
    queryFn: () => api.get<StrategyViews>("/strategy/example"),
    staleTime: Infinity,
  });
}

export function useStrategies() {
  return useQuery({
    queryKey: queryKeys.strategies,
    queryFn: () => api.get<StrategyRecord[]>("/strategies"),
  });
}

export function useSaveStrategy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id?: number; ir?: StrategyIR; dsl?: string; name?: string }) =>
      payload.id
        ? api.put<StrategyRecord>(`/strategies/${payload.id}`, payload)
        : api.post<StrategyRecord>("/strategies", payload),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

export function useDeleteStrategy() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/strategies/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.strategies }),
  });
}

// ---------------------------------------------------------------------- backtest

export function useRunBacktest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      strategy: { ir?: StrategyIR; dsl?: string };
      start: string;
      end?: string;
      allow_future?: boolean;
    }) => api.post<BacktestSummary>("/backtest", payload),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.runs }),
  });
}

export function useRuns() {
  return useQuery({
    queryKey: queryKeys.runs,
    queryFn: () => api.get<Record<string, unknown>[]>("/backtest/runs"),
  });
}

export function useRun(runId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.run(runId ?? ""),
    queryFn: () => api.get<BacktestSummary>(`/backtest/runs/${runId}`),
    enabled: Boolean(runId),
  });
}

export function useRunTrades(runId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.runTrades(runId ?? ""),
    queryFn: () => api.get<TradeRow[]>(`/backtest/runs/${runId}/trades`),
    enabled: Boolean(runId),
  });
}

export function useOptimize() {
  return useMutation({
    mutationFn: (payload: {
      strategy: { ir?: StrategyIR; dsl?: string };
      start: string;
      end?: string;
      trials?: number;
      objective?: string;
    }) => api.post<OptimizeJob>("/research/optimize", payload),
  });
}

export function useFactorAnalysis() {
  return useMutation({
    mutationFn: (payload: {
      expression: string;
      start: string;
      end?: string;
      horizon?: number;
      quantiles?: number;
      neutralize?: boolean;
    }) => api.post<FactorReport>("/research/factor", payload),
  });
}

// ---------------------------------------------------------------------- ops / ai

export function useHealth() {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: () => api.get<HealthResponse>("/health"),
    refetchInterval: 60_000,
    retry: false,
  });
}

export function useDataStatus() {
  return useQuery({
    queryKey: queryKeys.dataStatus,
    queryFn: () => api.get<DataStatusResponse>("/data/status"),
  });
}

export function useStartSync() {
  const client = useQueryClient();
  return useMutation({
    onMutate: async () => {
      await client.cancelQueries({ queryKey: queryKeys.activeSyncJob });
      client.setQueryData(queryKeys.activeSyncJob, null);
    },
    mutationFn: (payload: SyncInput) => api.post<SyncJob>("/data/sync", payload),
    onSuccess: (job) => client.setQueryData(queryKeys.activeSyncJob, job),
  });
}

export function useActiveSyncJob() {
  return useQuery({
    queryKey: queryKeys.activeSyncJob,
    queryFn: () => api.get<SyncJob | null>("/data/sync/active"),
    staleTime: 0,
    refetchInterval: 2000,
    refetchOnWindowFocus: "always",
  });
}

export function useSyncJob(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.syncJob(jobId ?? ""),
    queryFn: () => api.get<SyncJob>(`/data/sync/${jobId}`),
    enabled: Boolean(jobId),
    // Polling is the fallback; the SSE stream is the primary channel. Both exist
    // because SSE can be blocked by a proxy and a silent progress bar is worse than a
    // slow one.
    refetchInterval: (query) => {
      if (query.state.error instanceof ApiError && query.state.error.status === 404) return false;
      const status = query.state.data?.status;
      return status === "ok" || status === "failed" ? false : 2000;
    },
  });
}

export function useFullSyncSchedule() {
  return useQuery({
    queryKey: queryKeys.fullSyncSchedule,
    queryFn: () => api.get<FullSyncSchedule>("/data/full-sync-schedule"),
    refetchInterval: 5000,
  });
}

export function useSaveFullSyncSchedule() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (config: FullSyncScheduleConfig) =>
      api.put<FullSyncSchedule>("/data/full-sync-schedule", config),
    onSuccess: (schedule) => client.setQueryData(queryKeys.fullSyncSchedule, schedule),
  });
}

export function useRunFullSync() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<SyncJob>("/data/full-sync-schedule/run"),
    onSuccess: () => { void client.invalidateQueries({ queryKey: queryKeys.fullSyncSchedule }); },
  });
}

export function useAIFormula() {
  return useMutation({
    mutationFn: (payload: {
      description: string;
      kind?: "screener" | "indicator";
      validation_symbol?: string;
    }) => api.post<AIResponse>("/ai/formula", payload),
  });
}

export function useAIStrategy() {
  return useMutation({
    mutationFn: (payload: { description: string }) =>
      api.post<AIResponse>("/ai/strategy", payload),
  });
}

export function useAIExplain() {
  return useMutation({
    mutationFn: (runId: string) => api.post<AIResponse>(`/ai/explain/${runId}`),
  });
}
